import {
  BookingAddOnPaymentDto,
  BookingAddOnVendorDto,
  BookingAddOnVendorDtoStatusEnum,
  BookingAddOnsResponseDtoCannotOrderReasonEnum,
} from '@hqo/resource-booking-client';

/* Vendor add-on payments. The wire shapes come from `@hqo/resource-booking-client`; what lives here is
 * only what it cannot express — `paymentState` is a bare string, and `AddOnCharge` is narrower. */

/*
 * `satisfies` rejects a name that stops matching the generated union, but a subset satisfies it too —
 * a status the service ADDS compiles cleanly and then never matches. Diff
 * `BookingAddOnVendorDtoStatusEnum` by hand when the client is bumped.
 */
export const ADD_ON_PAYMENT_STATUS = {
  Pending: 'PENDING',
  /** The request exists and `workOrderUuid` is set. Only in this state is the uuid handed out. */
  Created: 'CREATED',
  /** The create never came back. The booking stands — the add-ons are ordered, the charge is not. */
  Failed: 'FAILED',
  /* Settled, not gone: a cancelled link still carries the payment block, which is where
   * `refundedAmountCents` and `payment_refunded` are reported. */
  Cancelled: 'CANCELLED',
} as const satisfies Record<string, BookingAddOnVendorDtoStatusEnum>;

/* The service returns a payment block for `CREATED || CANCELLED` and nothing else: `PENDING` has no
 * work order yet and `FAILED` never got one. */
export const hasPaymentBlock = (status: BookingAddOnVendorDtoStatusEnum | string | null | undefined): boolean =>
  status === ADD_ON_PAYMENT_STATUS.Created || status === ADD_ON_PAYMENT_STATUS.Cancelled;

/** The add-ons read, whole: the vendor rows plus the service's own verdict on ordering. */
export interface BookingAddOnsRead {
  /** A booking is limited to a single party, so this is empty or holds one. */
  vendors: Array<BookingAddOnVendorDto>;
  /** The service's verdict on whether a submission would be accepted. Never re-derived here. */
  canOrderAddOns: boolean;
  /** Populated only when `canOrderAddOns` is false, and it is the sentence rather than the gate. */
  cannotOrderReason: BookingAddOnsResponseDtoCannotOrderReasonEnum | null;
}

export const CANNOT_ORDER_REASON = {
  OrderInProgress: 'ORDER_IN_PROGRESS',
  OrderFailed: 'ORDER_FAILED',
  /** A stand-down is outstanding. Every money flag stays false until work-orders confirms it. */
  CancellationPending: 'CANCELLATION_PENDING',
} as const satisfies Record<string, BookingAddOnsResponseDtoCannotOrderReasonEnum>;

/*
 * Read off `BookingAddOnVendorDto.amendRefusedReason`, which the client types `string | null` — an open
 * set with no union to `satisfies` against. These are the four values work-orders emits today. An
 * unrecognised reason must still refuse; only `null` is permission. `amendRefusal` below is the only
 * reader, for that reason.
 */
export const AMEND_REFUSED_REASON = {
  VendorStarted: 'vendor_started',
  PaymentInProgress: 'payment_in_progress',
  /** Settled. The tenant can still cancel, refund or order again — just not amend this one. */
  Paid: 'paid',
  /** A checkout session is open and unpaid. It prices the old selection; amending would strand it. */
  PaymentOpen: 'payment_open',
} as const;

export type AmendRefusedReason = (typeof AMEND_REFUSED_REASON)[keyof typeof AMEND_REFUSED_REASON];

/* The vendor's refusal, narrowed to one this app has a sentence for, or `'unknown'`. Never null for a
 * non-null input: absence of a name is not absence of a refusal. */
export const amendRefusal = (reason: string | null | undefined): AmendRefusedReason | 'unknown' | null => {
  if (reason === null || reason === undefined || reason === '') {
    return null;
  }

  const known = Object.values(AMEND_REFUSED_REASON) as Array<string>;

  return known.includes(reason) ? (reason as AmendRefusedReason) : 'unknown';
};

/*
 * work-orders has not priced this order yet. Shared so the poll and the receipt agree about what
 * "being re-priced" means: one withholds the figure, the other keeps asking for it.
 */
export const REPRICING_DISPOSITION = 'not_yet_evaluated';

/* This order's money as one comparable value, held against a snapshot taken before an amend: for
 * seconds afterwards the read still describes the order as it WAS. Persisted columns only --
 * `estimatedTotalCents`, `estimatedTaxCents` and `cannotPayReason` are recomputed per request, so they
 * move while the order does not and would end the withhold on a superseded figure. The cost is that
 * an unpriced order has nothing here that moves, and waits its window out instead. */
export const paymentIdentity = (payment: BookingAddOnPaymentDto | null | undefined): string =>
  payment
    ? [
        payment.checkoutSessionUuid,
        payment.paymentState,
        payment.paymentDisposition,
        payment.totalCents,
        payment.amountDueCents,
        payment.hasUnpricedItems,
      ].join('|')
    : 'none';

export const WORK_ORDER_PAYMENT_STATE = {
  /** A session exists and the charge is outstanding. The poll after a charge waits on this clearing. */
  PendingPayment: 'pending_payment',
  Paid: 'paid',
  /** Settled and then given back. A state of its own in work-orders, not a flavour of `paid`. */
  PaymentRefunded: 'payment_refunded',
} as const;

/* Money moved — wider than "is this order settled". `payment_refunded` means a card was charged and
 * some of it came back, reported beside the figure; treating `paid` alone as settled hides it. */
const SETTLED_PAYMENT_STATES: ReadonlySet<string> = new Set([
  WORK_ORDER_PAYMENT_STATE.Paid,
  WORK_ORDER_PAYMENT_STATE.PaymentRefunded,
]);

/* Deliberately narrower than `chargeFrom`, which also insists on an amount and a currency because it
 * answers "can I render this": a settled order whose amount never arrived would poll forever if the
 * poll asked the render question. A type guard so `chargeFrom` keeps its narrowing. */
export const isSettledPaymentState = (paymentState: string | null | undefined): paymentState is string =>
  Boolean(paymentState) && SETTLED_PAYMENT_STATES.has(paymentState as string);

/** What the tenant paid, once they have. Null until the work order says money moved. */
export interface AddOnCharge {
  /** The raw `paymentState`, not a boolean, so a caller can tell a refund from a plain payment. */
  state: string;
  /** Minor units, like every other amount in this flow — `formatCurrency` divides by 100 itself. */
  totalCents: number;
  currency: string;
  /** Four digits, no brand: the tenant read exposes none. Absent on some gateways. */
  cardLastFour: string | null;
  /** ISO 8601, as work-orders sends it. */
  paidAt: string | null;
}

/* Only a settled work order yields a charge: every field is null while the state is `pending_payment`,
 * so the state is the gate, not the presence of the fields. Shared so the add-on step's poll and the
 * booking detail's one-shot read cannot disagree about whether a charge settled. */
export const chargeFrom = (payment: BookingAddOnPaymentDto): AddOnCharge | null => {
  if (!isSettledPaymentState(payment.paymentState)) {
    return null;
  }

  /* A paid order with no amount is not something to render a total from; fall back to the estimate. */
  if (typeof payment.totalCents !== 'number' || !payment.currency) {
    return null;
  }

  return {
    state: payment.paymentState,
    totalCents: payment.totalCents,
    currency: payment.currency,
    cardLastFour: payment.cardLastFour ?? null,
    paidAt: payment.paidAt ?? null,
  };
};
